import { ClickEvent, DataSourceExecutionEvent, DataSourceAction, TableRowCreationEvent, Textbox, LookupModelSelectionEvent, TableRow, Label, BlurEvent, Table } from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { toggleExpandablePanel, getFilteredRowCount } from "./DataFusionUtil";
import { AutogenLayoutLocationCustomerXref } from "./autogen/AutogenLayoutLocationCustomerXref";
import { ModelRow } from "@mcleod/core";

export class LocationCustomerXref extends AutogenLayoutLocationCustomerXref {
    private _panelEdiBillToExpanded: boolean = false;
    private _panelLocationExpanded: boolean = false;
    private labelBillToCount: Label;
    private labelLocationCount: Label;
    private _customerLabelTitle: string = "Customer";
    private _locationLabelTitle: string = "Location";

    override async onLoad() {
        await super.onLoad();

        // Create and add the count labels next to the main labels
        this.createCountLabels();

        // Set initial label formatting and collapse panels by default
        this.updateLabelFormatting(true);
    }

    private createCountLabels() {
        // Create the count label for BillTo
        this.labelBillToCount = new Label({
            text: "",
            color: "primary",
            fontBold: true,
            rowBreak: false,
            marginBottom: 0,
            paddingBottom: 0,
            fontSize: "large"
        });

        // Create the count label for Location
        this.labelLocationCount = new Label({
            text: "",
            color: "primary",
            fontBold: true,
            rowBreak: false,
            marginBottom: 0,
            paddingBottom: 0,
            fontSize: "large"
        });

        // Find the parent container of the BillTo label and insert the count label
        const billToLabelParent = this.labelBillTo.parent;
        if (billToLabelParent && this.horizontalspacerForEdiBilling) {
            const spacerIndex = billToLabelParent.indexOf(this.horizontalspacerForEdiBilling);
            if (spacerIndex >= 0) {
                billToLabelParent.insert(this.labelBillToCount, spacerIndex);
            } else {
                const labelIndex = billToLabelParent.indexOf(this.labelBillTo);
                if (labelIndex >= 0) {
                    billToLabelParent.insert(this.labelBillToCount, labelIndex + 1);
                }
            }
        }

        // Find the parent container of the Location label and insert the count label
        const locationLabelParent = this.labelLocation.parent;
        if (locationLabelParent && this.horizontalspacerForLocation) {
            const spacerIndex = locationLabelParent.indexOf(this.horizontalspacerForLocation);
            if (spacerIndex >= 0) {
                locationLabelParent.insert(this.labelLocationCount, spacerIndex);
            } else {
                const labelIndex = locationLabelParent.indexOf(this.labelLocation);
                if (labelIndex >= 0) {
                    locationLabelParent.insert(this.labelLocationCount, labelIndex + 1);
                }
            }
        }
    }

    /** This is an event handler for the onClick event of buttonBillToExpand. */
    async buttonBillToExpandOnClick(event: ClickEvent) {
        this._panelEdiBillToExpanded = await toggleExpandablePanel(
            this._panelEdiBillToExpanded,
            this.panelBillToCollapsible,
            this.buttonBillToExpand
        );
    }

    /** This is an event handler for the onClick event of buttonLocationExpand. */
    async buttonLocationExpandOnClick(event: ClickEvent) {
        this._panelLocationExpanded = await toggleExpandablePanel(
            this._panelLocationExpanded,
            this.panelLocationCollapsible,
            this.buttonLocationExpand
        );
    }

    /** This is an event handler for the afterExecution event of sourceEdilocation. */
    sourceEdilocationAfterExecution(event: DataSourceExecutionEvent) {
        // Only apply filtering after search operations and when we have data
        if (event.getAction() === DataSourceAction.SEARCH) {
            // Use setTimeout to ensure the filtering happens after the data is fully loaded
            setTimeout(() => {
                this.filterBillToRecords();
                this.filterLocationRecords();
                this.updateLabelFormatting();
            }, 100);
        }
    }

    private filterBillToRecords() {
        // Set a custom filter on tableBillTo to show only records where location_id is null and customer_id is not null
        this.tableBillTo.customFilter = (tableRow) => {
            const locationId = tableRow.data.get("location_id");
            const customerId = tableRow.data.get("customer_id");
            // Show records where location_id is null/empty and customer_id has a value
            return (locationId === null || locationId === undefined || locationId === "") &&
                (customerId !== null && customerId !== undefined && customerId !== "");
        };
    }

    private filterLocationRecords() {
        // Set a custom filter on tableLocation to show only records where location_id is not null
        this.tableLocation.customFilter = (tableRow) => {
            const locationId = tableRow.data.get("location_id");
            // Show records where location_id has a value
            return (locationId !== null && locationId !== undefined && locationId !== "");
        };
    }

    private updateLabelFormatting(forceCollapse: boolean = false) {
        // Get filtered row counts instead of total row counts
        const billToVisibleRows = getFilteredRowCount(this.tableBillTo);
        const locationVisibleRows = getFilteredRowCount(this.tableLocation);

        // Set label formatting based on visible/filtered rows
        this.labelBillTo.fontBold = billToVisibleRows > 0;
        this.labelLocation.fontBold = locationVisibleRows > 0;

        // Set the main label text without count
        this.labelBillTo.text = this._customerLabelTitle;
        this.labelLocation.text = this._locationLabelTitle;

        // Set the count label text and visibility for BillTo
        if (this.labelBillToCount) {
            this.labelBillToCount.text = billToVisibleRows > 0 ? billToVisibleRows.toString() : "";
            this.labelBillToCount.visible = billToVisibleRows > 0;
        }

        // Set the count label text and visibility for Location
        if (this.labelLocationCount) {
            this.labelLocationCount.text = locationVisibleRows > 0 ? locationVisibleRows.toString() : "";
            this.labelLocationCount.visible = locationVisibleRows > 0;
        }

        // Handle panel expansion/collapse
        if (forceCollapse) {
            setTimeout(() => {
                this.panelBillToCollapsible.collapse({ speed: 100, paddingTop: 0 }).then(() => {
                    this.buttonBillToExpand.imageRotation = 90;
                });
                this._panelEdiBillToExpanded = false;

                this.panelLocationCollapsible.collapse({ speed: 100, paddingTop: 0 }).then(() => {
                    this.buttonLocationExpand.imageRotation = 90;
                });
                this._panelLocationExpanded = false;
            }, 1);
        }
    }

    /** This is an event handler for the onRowCreate event of tableBillTo. */
    tableBillToOnRowCreate(event: TableRowCreationEvent) {
        // Access the parent datasource (sourceEdiorderProfileDisplay) through owner.mainDataSource
        if (this.owner && this.owner.mainDataSource && this.owner.mainDataSource.activeRow) {
            const parentRow = this.owner.mainDataSource.activeRow;
            const newRow = event.getTableRow().data;

            // IMPORTANT: Reset original data FIRST, before setting values, to prevent marking datasource as changed
            newRow.resetOriginalData();

            // Set the required fields from parent
            newRow.set("partner_id", parentRow.get("partner_id"));
            newRow.set("version", parentRow.get("version"));

            // Reset again after setting to clear the dirty flag
            newRow.resetOriginalData();

            // Force the datasource to recalculate its hasChanged state
            this.sourceEdilocation?.notifyHasChangedComponents(false);
        }

        // Update label formatting after row creation
        setTimeout(() => this.updateLabelFormatting(), 10);
    }

    /** This is an event handler for the onRowCreate event of tableLocation. */
    tableLocationOnRowCreate(event: TableRowCreationEvent) {
        // Access the parent datasource (sourceEdiorderProfileDisplay) through owner.mainDataSource
        if (this.owner && this.owner.mainDataSource && this.owner.mainDataSource.activeRow) {
            const parentRow = this.owner.mainDataSource.activeRow;
            const newRow = event.getTableRow().data;

            // IMPORTANT: Reset original data FIRST, before setting values, to prevent marking datasource as changed
            newRow.resetOriginalData();

            // Set the required fields from parent
            newRow.set("partner_id", parentRow.get("partner_id"));
            newRow.set("version", parentRow.get("version"));

            // Reset again after setting to clear the dirty flag
            newRow.resetOriginalData();

            // Force the datasource to recalculate its hasChanged state
            this.sourceEdilocation?.notifyHasChangedComponents(false);
        }

        // Update label formatting after row creation
        setTimeout(() => this.updateLabelFormatting(), 10);
    }    /** This is an event handler for the onContentsChanged event of tableBillTo. */
    tableBillToOnContentsChanged(event) {
        // Update label formatting when table contents change (add/delete rows)
        setTimeout(() => this.updateLabelFormatting(), 10);
    }

    /** This is an event handler for the onContentsChanged event of tableLocation. */
    tableLocationOnContentsChanged(event) {
        // Update label formatting when table contents change (add/delete rows)
        setTimeout(() => this.updateLabelFormatting(), 10);
    }



    /** This is an event handler for the onLookupModelSelection event of textboxCustomerId. */
    textboxCustomerIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        // Get the selected customer data
        if (event.hasSelection()) {
            const selectedCustomer = event.selection;
            const customerName = selectedCustomer.get("name");

            const tableRow = TableRow.getContainingTableRow(event.target);
            const textboxName = tableRow?.findComponentById("textboxCustomerName") as Textbox;
            if (textboxName) {
                textboxName.text = customerName;
            }
        }
    }

    /** This is an event handler for the onLookupModelSelection event of textboxLocationCode. */
    textboxLocationCodeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        // textboxLocationName
        // Get the selected customer data
        if (event.hasSelection()) {
            const selectedLocation = event.selection;
            const locationName = selectedLocation.get("name");

            const tableRow = TableRow.getContainingTableRow(event.target);
            const textboxName = tableRow?.findComponentById("textboxLocationName") as Textbox;
            if (textboxName) {
                textboxName.text = locationName;
            }
        }
    }

    async onBlurEdiLocation(event: BlurEvent) {
        await this.onBlurLocationCustomerCrossReference(event, this.tableLocation, true);
    }

    async onBlurEdiCustomer(event: BlurEvent) {
        await this.onBlurLocationCustomerCrossReference(event, this.tableBillTo, false);
    }

    async onBlurLocationCustomerCrossReference(event: BlurEvent, table: Table, isLocation: boolean) {
        const textbox = event.target as Textbox;
        const newTableRow = TableRow.getContainingTableRow(textbox);
        const newRow = newTableRow.data as ModelRow;
        const newText = textbox.text;
        if (newText) {
            const existingRows = table.data;
            let filteredRows: ModelRow<any>[] = [];
            if (isLocation) {
                filteredRows = existingRows.filter((row) =>
                    row.get("location_id") !== null && row.get("location_id") !== undefined && row.get("location_id") !== ""
                );
            } else {
                filteredRows = existingRows.filter((row) =>
                    (row.get("location_id") === null || row.get("location_id") === undefined || row.get("location_id") === "") && 
                    (row.get("customer_id") !== null && row.get("customer_id") !== undefined && row.get("customer_id") !== "")
                );
            }
            const isDuplicate = filteredRows.find((row) =>
                row.get("edi_location") === newText && row !== newRow
            );
            if (isDuplicate) {
                // Show error message to user
                const yesNo = await CommonDialogs.showYesNo(`The Partner Code "${newText}" already exists in the table, are you sure you want to add another?`);
                if (!yesNo) {
                    textbox.text = null;
                }
            }
        }
    }

}
